import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../App";
import { useAuth } from "../contexts/AuthContext";
import { LogOut, Shield, KeyRound, Menu, X } from "lucide-react";
import ChangePasswordModal from "./ChangePasswordModal";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();
  const { user, logout, setShowAuthModal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navClass = isHome && !scrolled ? "navbar-transparent" : "navbar-solid";

  const goTo = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogin = () => {
    setShowAuthModal(true);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
  };

  return (
    <>
      <nav className={`navbar ${navClass}`} data-testid="navbar">
        <div
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            padding: "0 1rem",
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button
            data-testid="navbar-logo"
            onClick={() => goTo("/")}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}
          >
            <span
              className="font-logo"
              style={{
                fontSize: "clamp(1.2rem, 2.5vw, 1.9rem)",
                fontWeight: 500,
                letterSpacing: "0.3em",
                color: "#D4B878",
                textTransform: "uppercase",
              }}
            >
              KHATIRA
            </span>
          </button>

          {/* Desktop nav */}
          <div className="nav-desktop" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <div className="lang-toggle" data-testid="lang-toggle">
              <button className={`lang-btn ${lang === "en" ? "active" : ""}`} data-testid="lang-en-btn" onClick={() => setLang("en")}>EN</button>
              <button className={`lang-btn ${lang === "ar" ? "active" : ""}`} data-testid="lang-ar-btn" onClick={() => setLang("ar")}>AR</button>
            </div>
            {user ? (
              <>
                {user.role === "admin" && (
                  <button data-testid="navbar-admin-btn" onClick={() => goTo("/admin")} className="btn-ghost" style={{ padding: "0.4rem 0.7rem", fontSize: "0.72rem", letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                    <Shield size={13} /> {t("admin_title")}
                  </button>
                )}
                <button data-testid="navbar-studio-btn" className="btn-primary" style={{ padding: "0.5rem 1.25rem", fontSize: "0.82rem", letterSpacing: "0.12em" }} onClick={() => goTo("/studio")}>
                  {t("nav_open_studio")}
                </button>
                <span data-testid="navbar-user-name" style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: "#B8956A", maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</span>
                <button data-testid="navbar-change-password-btn" onClick={() => setShowPwdModal(true)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(184,149,106,0.5)", padding: "0.25rem", display: "flex" }} title={t("auth_change_password")}><KeyRound size={15} /></button>
                <button data-testid="navbar-logout-btn" onClick={handleLogout} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(184,149,106,0.5)", padding: "0.25rem", display: "flex" }} title={t("auth_logout")}><LogOut size={16} /></button>
              </>
            ) : (
              <>
                <button data-testid="navbar-login-btn" className="btn-ghost" style={{ padding: "0.45rem 1rem", fontSize: "0.82rem", letterSpacing: "0.1em" }} onClick={handleLogin}>{t("auth_login")}</button>
                <button data-testid="navbar-studio-btn" className="btn-primary" style={{ padding: "0.5rem 1.25rem", fontSize: "0.82rem", letterSpacing: "0.12em" }} onClick={() => goTo("/studio")}>{t("nav_open_studio")}</button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            data-testid="navbar-mobile-toggle"
            className="nav-mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#D4B878", padding: "0.25rem", display: "none" }}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="navbar-mobile-menu"
          style={{
            position: "fixed",
            top: "64px",
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(13,7,4,0.97)",
            backdropFilter: "blur(12px)",
            zIndex: 999,
            display: "flex",
            flexDirection: "column",
            padding: "2rem 1.5rem",
            gap: "1rem",
            animation: "fadeIn 0.2s ease",
          }}
        >
          <div className="lang-toggle" style={{ alignSelf: "flex-start" }}>
            <button className={`lang-btn ${lang === "en" ? "active" : ""}`} onClick={() => setLang("en")}>EN</button>
            <button className={`lang-btn ${lang === "ar" ? "active" : ""}`} onClick={() => setLang("ar")}>AR</button>
          </div>

          <div style={{ borderTop: "1px solid rgba(201,168,76,0.12)", margin: "0.5rem 0" }} />

          <button className="btn-primary" style={{ padding: "0.85rem", fontSize: "0.88rem", letterSpacing: "0.15em", width: "100%" }} onClick={() => goTo("/studio")}>
            {t("nav_open_studio")}
          </button>

          {user ? (
            <>
              {user.role === "admin" && (
                <button className="btn-ghost" style={{ padding: "0.75rem", fontSize: "0.85rem", letterSpacing: "0.1em", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }} onClick={() => goTo("/admin")}>
                  <Shield size={15} /> {t("admin_title")}
                </button>
              )}
              <button className="btn-ghost" style={{ padding: "0.75rem", fontSize: "0.85rem", letterSpacing: "0.1em", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }} onClick={() => { setShowPwdModal(true); setMobileOpen(false); }}>
                <KeyRound size={15} /> {t("auth_change_password")}
              </button>

              <div style={{ borderTop: "1px solid rgba(201,168,76,0.12)", margin: "0.5rem 0" }} />

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.88rem", color: "#B8956A" }}>{user.name}</span>
                <button onClick={handleLogout} style={{ background: "none", border: "none", cursor: "pointer", color: "#C9A84C", fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                  <LogOut size={15} /> {t("auth_logout")}
                </button>
              </div>
            </>
          ) : (
            <button className="btn-ghost" style={{ padding: "0.75rem", fontSize: "0.88rem", letterSpacing: "0.1em", width: "100%" }} onClick={handleLogin}>
              {t("auth_login")}
            </button>
          )}
        </div>
      )}

      <ChangePasswordModal open={showPwdModal} onClose={() => setShowPwdModal(false)} />
    </>
  );
}
